import type { Money } from "@/types/api"
import { currentLocale, translate } from "@/i18n"

export function formatMoney(value: Money | null | undefined): string {
  if (!value) return "—"
  return new Intl.NumberFormat(currentLocale() === "ru" ? "ru-RU" : "en-US", {
    style: "currency",
    currency: "USD",
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 12,
  }).format(value.amount)
}

export function moneyFromUsd(value: string): Money {
  const normalized = value.trim()
  if (!/^[+-]?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)(?:[eE][+-]?[0-9]+)?$/.test(normalized)) {
    throw new Error(translate("USD amount must be a valid number."))
  }
  const amount = Number(normalized)
  if (!Number.isFinite(amount)) throw new Error(translate("USD amount must be finite."))
  return { amount }
}

export function moneyToUsd(value: Money | null | undefined): string {
  return value ? String(value.amount) : ""
}

export function formatJson(value: unknown): string {
  return JSON.stringify(value, null, 2) ?? "undefined"
}

export function formatNumber(value: number | null | undefined): string {
  return value == null ? "—" : new Intl.NumberFormat(currentLocale() === "ru" ? "ru-RU" : "en-US").format(value)
}

export function formatDate(value: string | null | undefined, includeTime = true): string {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(currentLocale() === "ru" ? "ru-RU" : "en-US", includeTime
    ? { dateStyle: "medium", timeStyle: "short" }
    : { dateStyle: "medium" }).format(date)
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(currentLocale() === "ru" ? "ru-RU" : "en-US", { timeStyle: "short" }).format(date)
}

export function isoStartOfDaysAgo(days: number): string {
  const date = new Date()
  date.setUTCDate(date.getUTCDate() - days)
  date.setUTCHours(0, 0, 0, 0)
  return date.toISOString()
}

export function queryString(query?: Record<string, string | number | boolean | null | undefined | readonly string[]>): string {
  if (!query) return ""
  const params = new URLSearchParams()
  Object.entries(query).forEach(([key, raw]) => {
    if (raw == null || raw === "") return
    const values = Array.isArray(raw) ? raw : [raw]
    values.forEach((value) => params.append(key, String(value)))
  })
  const result = params.toString()
  return result ? `?${result}` : ""
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

export function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("") || "CN"
}
